import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const WOMEN_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface WomenEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  image: string;
}

export const WOMEN_ENTRIES: WomenEntry[] = [];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=400&fit=crop";

function mapsUrl(e: WomenEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function WomenCard({ entry, index }: { entry: WomenEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[180px] relative overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            {entry.name}
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        <SocialLinks item={entry} px={20} />
        {entry.website && (
          <form action={entry.website} method="get" target="_top" className="w-full">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Visit Website
            </button>
          </form>
        )}
        {entry.address && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Women() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("women", WOMEN_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Women"
        placeholder="Search women's organizations & groups…"
        backgroundImage="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Women's Organizations & Groups
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Discover women's organizations, groups, and resources in the Dallas-DFW area.
            </p>
            <AdminAddBar mc={mc} fields={WOMEN_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={WOMEN_FIELDS} title="Listing">
                    <WomenCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No women's organization listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
